import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  Button
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/domains/api';
import type { IRefreshRunRow } from '../../api/brokercheck-refresh-contracts';
import { brokerCheckRefreshApi, refreshRefusal } from '../../api/brokercheck-refresh-api';
import { runStateChip } from '../../brokercheck-refresh';
import { refreshRunPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { sentence, unanswered, type ISaidNote } from '../person-hub/said';

/**
 * The refresh's two writes — Run now and Cancel (SR-222, F10's R8) — each a confirmed press, and
 * every answer each can bring told apart by status and `type` (`refreshRefusal`), never by
 * `title`.
 *
 * **Neither pre-judges survey-api.** Run now is drawn always: whether a run may start is the
 * one-active-run index's to say, and a list a page old cannot. Cancel is drawn on an active state
 * (`isRunActive`), the one thing the page reads off the run, and a run that finished meanwhile is
 * survey-api's 409 `invalid-transition`, said and re-read.
 *
 * **An answer that never arrived re-reads and asks to be checked** — the hub's rule: a retried
 * start whose first did land would be refused `run-in-progress`, and a retried cancellation
 * `invalid-transition`, each blaming a page that was merely read before the write.
 *
 * **Both buttons are held while either write is out** (`RefreshWriteHold`, provided by the runs
 * area): Run now sits on the list and Cancel on a run, and Esc or a row's link moves between the
 * two while a write is on its way.
 */

/** What a write was told, with the run a refusal names — `run-in-progress`'s active run. */
export interface IRunNote extends ISaidNote {
  runId?: string;
}

/** What a write said, where it was pressed — and, for a refusal naming the active run, the way
 *  to it, kept in the list's search so the list stays beside it. */
export function RunNoteLine({ note, search }: { note: IRunNote; search: string }) {
  return (
    <Alert variant={note.tone === 'refused' ? 'destructive' : 'default'}>
      {note.title && <AlertTitle>{note.title}</AlertTitle>}
      <AlertDescription>
        <span>{note.text}</span>
        {note.runId && (
          <Link
            to={refreshRunPath(note.runId)}
            state={{ search, list: true }}
            className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            Open the run in progress
          </Link>
        )}
      </AlertDescription>
    </Alert>
  );
}

const WriteHold = React.createContext<{ writing: boolean; hold: (on: boolean) => void }>({
  writing: false,
  hold: () => undefined
});

/** The runs area's hold over its two writes — see the module's comment. */
export function RefreshWriteHold({ children }: { children: React.ReactNode }) {
  const [writes, setWrites] = React.useState(0);
  const value = React.useMemo(
    () => ({
      writing: writes > 0,
      hold: (on: boolean) => setWrites((count) => Math.max(0, count + (on ? 1 : -1)))
    }),
    [writes]
  );
  return <WriteHold.Provider value={value}>{children}</WriteHold.Provider>;
}

function useWriteHold() {
  return React.useContext(WriteHold);
}

/** The words of a write that did not land and is not one of the refusals named. */
function otherRefusal(error: unknown, title: string, fallback: string): IRunNote {
  return { tone: 'refused', title, text: sentence(refusalMessage(error, fallback)) };
}

/**
 * Run now, in the Runs tab's header. 202: the run opened (`onStarted`), the list beside it read
 * afresh by its pane. `run-in-progress`: said with the way to the active run, and the list read
 * again; `anonymized-target`: survey-api's words, nothing read again.
 */
export function RunNowButton({
  onStarted,
  onSaid,
  reread
}: {
  onStarted: (run: IRefreshRunRow) => void;
  onSaid: (note: Nullable<IRunNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const { writing, hold } = useWriteHold();
  const [confirming, setConfirming] = React.useState(false);
  const [starting, setStarting] = React.useState(false);

  const start = async () => {
    setStarting(true);
    hold(true);
    onSaid(null);
    try {
      const run = await brokerCheckRefreshApi.startRun();
      setConfirming(false);
      onStarted(run);
    } catch (error) {
      setConfirming(false);
      onSaid(await startTrouble(error, reread));
    } finally {
      setStarting(false);
      hold(false);
    }
  };

  return (
    <AlertDialog open={confirming} onOpenChange={(open) => !starting && setConfirming(open)}>
      <AlertDialogTrigger asChild>
        {/* `data-slot` said again: the trigger's own would replace the kit's (the photo's). */}
        <Button type="button" size="sm" data-slot="button" disabled={writing}>
          Run now
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Start a BrokerCheck refresh run now?</AlertDialogTitle>
          <AlertDialogDescription>
            It asks FINRA’s BrokerCheck about every CRD the platform holds, one a second, with no
            window: it goes on until it is done or cancelled, whatever the schedule says. The work
            items its answers raise land in the compliance queues.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={starting}>Keep things as they are</AlertDialogCancel>
          <AlertDialogAction
            disabled={starting}
            onClick={(event) => {
              // Held open until survey-api answers, as the hub's confirmations are.
              event.preventDefault();
              void start();
            }}
          >
            {starting ? 'Starting…' : 'Start the run'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

async function startTrouble(error: unknown, reread: () => Promise<boolean>): Promise<IRunNote> {
  const refusal = refreshRefusal(error);
  if (refusal?.kind === 'run-in-progress') {
    const read = await reread();
    return {
      tone: 'refused',
      title: 'Not started',
      text:
        'A run is already in progress, and only one runs at a time — cancel it or let it ' +
        `finish first.${read ? '' : ' The list could not be read again.'}`,
      runId: refusal.runId ?? undefined
    };
  }
  if (refusal?.kind === 'anonymized-target') {
    return {
      tone: 'refused',
      title: 'Not started',
      text: sentence(refusal.title ?? 'This database was loaded anonymized')
    };
  }
  if (unanswered(error)) {
    const read = await reread();
    return {
      tone: 'refused',
      title: 'No answer',
      text: read
        ? 'The answer did not arrive — the list has been read again: check whether a run ' +
          'started before pressing Run now again.'
        : 'The answer did not arrive, and the list could not be read again — reload to see ' +
          'whether a run started.'
    };
  }
  return otherRefusal(error, 'Not started', 'Could not start a run');
}

/**
 * Cancel, on an active run's record. 200: the run as cancelled handed back (`onCancelled`) — the
 * pane adopts it and the list is read again. `invalid-transition`: "already <state>", off
 * survey-api's `reason`; a 404 "no longer there"; both with the run read again (`reread`).
 *
 * **The dialog outlives its button** (gate 2, F8's Remove): the button is drawn on an active run
 * alone (`active`), and a press that lands — or a refusal whose re-read finds the run finished —
 * takes it away, which left the focus on the page's body. So the dialog stays mounted whatever the
 * state, and once a press was made it hands the focus to `focusAfter` — the run's state — rather
 * than to a trigger that may be gone. A dismissal returns it to the button, as Radix does.
 */
export function CancelRunButton({
  runId,
  active,
  focusAfter,
  onCancelled,
  onSaid,
  reread
}: {
  runId: string;
  /** Whether the run is in a state Cancel is drawn on (`isRunActive`). */
  active: boolean;
  focusAfter: () => void;
  onCancelled: (run: IRefreshRunRow) => void;
  onSaid: (note: Nullable<IRunNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const { writing, hold } = useWriteHold();
  const [confirming, setConfirming] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const pressed = React.useRef(false);

  const cancel = async () => {
    pressed.current = true;
    setCancelling(true);
    hold(true);
    onSaid(null);
    try {
      const run = await brokerCheckRefreshApi.cancelRun(runId);
      setConfirming(false);
      onCancelled(run);
    } catch (error) {
      setConfirming(false);
      onSaid(await cancelTrouble(error, reread));
    } finally {
      setCancelling(false);
      hold(false);
    }
  };

  return (
    <AlertDialog open={confirming} onOpenChange={(open) => !cancelling && setConfirming(open)}>
      {active && (
        <AlertDialogTrigger asChild>
          <Button type="button" size="sm" variant="outline" data-slot="button" disabled={writing}>
            Cancel run
          </Button>
        </AlertDialogTrigger>
      )}
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          if (!pressed.current) return;
          pressed.current = false;
          event.preventDefault();
          focusAfter();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this run?</AlertDialogTitle>
          <AlertDialogDescription>
            It stops where it is. No route resumes a cancelled run: the CRDs it has not reached
            wait for the next run, scheduled or started with Run now.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelling}>Keep it running</AlertDialogCancel>
          <AlertDialogAction
            disabled={cancelling}
            onClick={(event) => {
              event.preventDefault();
              void cancel();
            }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel the run'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

async function cancelTrouble(error: unknown, reread: () => Promise<boolean>): Promise<IRunNote> {
  const refusal = refreshRefusal(error);
  const rereadSaying = async (title: string, text: string): Promise<IRunNote> => {
    const read = await reread();
    return {
      tone: 'refused',
      title,
      text: text + (read ? ' It has been read again.' : ' It could not be read again — reload.')
    };
  };
  if (refusal?.kind === 'invalid-transition') {
    const state = refusal.state === null ? 'finished' : runStateChip(refusal.state).label;
    return rereadSaying('Not cancelled', `This run is already ${state.toLowerCase()}.`);
  }
  if (error instanceof ApiError && error.status === 404) {
    return rereadSaying('Not cancelled', 'This run is no longer there.');
  }
  if (unanswered(error)) {
    return rereadSaying(
      'No answer',
      'The answer did not arrive — check whether the run was cancelled before trying again.'
    );
  }
  return otherRefusal(error, 'Not cancelled', 'Could not cancel the run');
}
